import Phaser from "phaser";

import { OfflineRewardScene, type OfflineRewardSceneCallbacks } from "./OfflineRewardScene";

export type OfflineRewardRuntime = {
  game: Phaser.Game;
  scene: OfflineRewardScene;
  ready: Promise<void>;
  destroy(): void;
};

const MAX_RENDER_SCALE = 2;

export function createOfflineRewardGame(
  parent: HTMLElement,
  callbacks: OfflineRewardSceneCallbacks,
  reducedMotion: boolean,
): OfflineRewardRuntime {
  const renderScale = Math.min(MAX_RENDER_SCALE, Math.max(1, window.devicePixelRatio || 1));
  const logicalWidth = parent.clientWidth || 360;
  const logicalHeight = parent.clientHeight || 800;
  let resolveReady: () => void = () => undefined;
  let readyFrame = 0;
  let lastReadySize = "";
  let stableReadyFrames = 0;
  let outerRevolutions = 0;
  let innerRevolutions = 0;
  const ready = new Promise<void>((resolve) => {
    resolveReady = resolve;
  });
  const settleReadyState = (): void => {
    const size = `${parent.clientWidth}x${parent.clientHeight}`;
    stableReadyFrames = size === lastReadySize ? stableReadyFrames + 1 : 0;
    lastReadySize = size;
    if (stableReadyFrames < 2) {
      readyFrame = window.requestAnimationFrame(settleReadyState);
      return;
    }
    parent.dataset.phaserReady = "true";
    parent.dataset.phaserLoad = "ready";
    parent.dataset.phaserLoadProgress = "1.000";
    resolveReady();
    callbacks.onReady?.();
  };
  const scene = new OfflineRewardScene(
    {
      ...callbacks,
      onReady: () => {
        window.cancelAnimationFrame(readyFrame);
        readyFrame = window.requestAnimationFrame(settleReadyState);
      },
      onLoadProgress: (progress) => {
        parent.dataset.phaserLoadProgress = progress.toFixed(3);
        callbacks.onLoadProgress?.(progress);
      },
      onLoadError: () => {
        parent.dataset.phaserLoad = "error";
        callbacks.onLoadError?.();
      },
      onRevolution: (ring) => {
        if (ring === "outer") {
          outerRevolutions += 1;
          parent.dataset.phaserOuterRevolutions = String(outerRevolutions);
        } else {
          innerRevolutions += 1;
          parent.dataset.phaserInnerRevolutions = String(innerRevolutions);
        }
        callbacks.onRevolution?.(ring);
      },
    },
    reducedMotion,
    renderScale,
  );
  parent.dataset.phaserScene = "offline-astrolabe-v7";
  parent.dataset.phaserMotion = reducedMotion ? "reduced" : "full";
  parent.dataset.phaserClearance = "radial-shell-depth-sort";
  parent.dataset.phaserRotation = "independent-3d";
  parent.dataset.phaserOuterOrbit = "tilted-contour";
  parent.dataset.phaserBob = "single-sine";
  parent.dataset.phaserRadii = "122:98:80";
  parent.dataset.phaserOuterRevolutions = "0";
  parent.dataset.phaserInnerRevolutions = "0";
  parent.dataset.phaserReady = "false";
  parent.dataset.phaserLoad = "loading";
  parent.dataset.phaserLoadProgress = "0.000";
  const game = new Phaser.Game({
    type: Phaser.CANVAS,
    // CanvasPool also serves transparent scenes. A 2D context created with
    // alpha:false keeps that attribute after resizing/reuse, turning the next
    // energy mechanism into a black rectangle. Keep this opaque poster private.
    canvas: document.createElement("canvas"),
    parent,
    width: Math.round(logicalWidth * renderScale),
    height: Math.round(logicalHeight * renderScale),
    transparent: false,
    antialias: true,
    backgroundColor: "#092631",
    render: {
      antialias: true,
      pixelArt: false,
      roundPixels: false,
    },
    scale: {
      mode: Phaser.Scale.NONE,
      autoCenter: Phaser.Scale.NO_CENTER,
    },
    input: { activePointers: 2 },
    scene: [scene],
    banner: false,
  });

  let resizeFrame = 0;
  const resize = (): void => {
    window.cancelAnimationFrame(resizeFrame);
    resizeFrame = window.requestAnimationFrame(() => {
      if (!game.isBooted) return;
      const width = Math.round(parent.clientWidth);
      const height = Math.round(parent.clientHeight);
      if (width > 0 && height > 0) game.scale.resize(Math.round(width * renderScale), Math.round(height * renderScale));
    });
  };
  window.addEventListener("resize", resize, { passive: true });
  window.visualViewport?.addEventListener("resize", resize, { passive: true });

  return {
    game,
    scene,
    ready,
    destroy(): void {
      window.cancelAnimationFrame(readyFrame);
      window.cancelAnimationFrame(resizeFrame);
      window.removeEventListener("resize", resize);
      window.visualViewport?.removeEventListener("resize", resize);
      delete parent.dataset.phaserScene;
      delete parent.dataset.phaserMotion;
      delete parent.dataset.phaserClearance;
      delete parent.dataset.phaserRotation;
      delete parent.dataset.phaserOuterOrbit;
      delete parent.dataset.phaserBob;
      delete parent.dataset.phaserRadii;
      delete parent.dataset.phaserOuterRevolutions;
      delete parent.dataset.phaserInnerRevolutions;
      delete parent.dataset.phaserReady;
      delete parent.dataset.phaserLoad;
      delete parent.dataset.phaserLoadProgress;
      game.destroy(true);
    },
  };
}
